import React, { useState, useEffect } from 'react'
import { DateRange } from 'react-date-range'
import 'react-date-range/dist/styles.css' // main style file
import 'react-date-range/dist/theme/default.css' // theme css file
import moment from 'moment'
import shortid from 'shortid'

import Modal, { ModalHead, ModalBody, ModalFooter } from './modal'
import Heading from './heading'
import { clientInstance } from '../store/sagas'
import {
  formatForAPI,
  ddmmyyyyToDate,
  yyyymmddToDD_MM_YYYY,
  apiDateStringToDate,
} from '../helpers/dates'
import styles from '../component-styles/calendar-create-campaign.module.scss'
import Input, { Label } from './input'
import SelectBoxTypeahead from './select-box-typeahead'
import ConfirmationButtons from './confirmation-buttons'

const yesterday = new Date()
yesterday.setDate(yesterday.getDate() - 1)

export async function onSubmit(
  formData: any,
  onSuccess: () => void,
  update?: false,
): Promise<void> {
  const formEndDate =
    formData.campaignEndDate ||
    moment(formData.campaignStartDate || '')
      .add(1, 'day')
      .toDate()

  const formDataWithDate = {
    ...formData,
    startDate: formatForAPI(formData.campaignStartDate),
    endDate: formatForAPI(ddmmyyyyToDate(formEndDate)),
  }

  try {
    let res
    if (update) {
      res = await clientInstance.updateCampaign(formDataWithDate)
    } else {
      res = await clientInstance.createNewCampaign(formDataWithDate)
    }

    if (res) {
      onSuccess()
    }
  } catch (e) {
    console.error(e)
  }
}

export async function onDelete(
  eventID: string,
  onSuccess: () => void,
): Promise<void> {
  try {
    const res = await clientInstance.deleteCampaign({ eventID })

    if (res) {
      onSuccess()
    }
  } catch (e) {
    console.error(e)
  }
}

export const initialState = {
  campaignTitle: '',
  campaignEndDate: '',
  campaignStartDate: '',
  campaignData: '',
  campaignGATitle: '',
}

interface Props {
  active: boolean
  startDate: Date
  onSuccess: () => void
  toggleActive: () => void
  gaCampaigns: string[]
  campaign: any
}

export default function CreateCampaign({
  active,
  toggleActive,
  onSuccess,
  startDate,
  gaCampaigns,
  campaign,
}: Props): React.ReactElement | null {
  const defaultCampaign = {
    ...(campaign !== null
      ? {
          ...campaign,
          campaignStartDate: campaign.startDate,
          campaignEndDate: campaign.endDate,
        }
      : initialState),
  }
  const [formData, setFormData] = useState(
    defaultCampaign || {
      ...initialState,
      campaignStartDate: startDate,
    },
  )
  const [key, setKey] = useState(shortid.generate())

  let datePickerStartDate
  if (campaign !== null) {
    datePickerStartDate = apiDateStringToDate(formData.campaignStartDate)
  } else {
    datePickerStartDate = new Date(formData.campaignStartDate || '')
  }

  useEffect((): void => {
    setFormData({ ...initialState, campaignStartDate: startDate })
    // eslint-disable-next-line
  }, [])

  useEffect((): void => {
    if (campaign) {
      setFormData({
        ...campaign,
        campaignStartDate: campaign.startDate,
        campaignEndDate: yyyymmddToDD_MM_YYYY(campaign.endDate),
      })
    }
    setKey(shortid.generate())
  }, [campaign])

  const updateForm = (type: string, next: string): void =>
    setFormData({
      ...formData,
      [type]: next,
    })

  const selectionRange = {
    startDate: datePickerStartDate,
    endDate: formData.campaignEndDate
      ? moment(formData.campaignEndDate, 'DD-MM-YYYY').toDate()
      : moment(formData.campaignStartDate || '')
          .add(1, 'day')
          .toDate(),
    key: 'dates',
  }

  if (!formData.campaignStartDate) return null

  if (!active) {
    return <></>
  }

  return (
    <Modal key={key} active={active} toggleActive={toggleActive} narrow>
      <ModalHead>
        <Heading type={3} align="left">
          {campaign !== null ? 'Edit campaign' : 'Add a campaign'}
        </Heading>
        <p className={styles.subTitle}>
          Remind everyone of live campaigns which could impact performance
          and/or analytics over a period of time
        </p>
      </ModalHead>
      <form
        onSubmit={(e): void => {
          e.preventDefault()
          onSubmit(formData, onSuccess)
          toggleActive()
        }}
      >
        <ModalBody>
          <Label modalHeading id="title">
            Campaign title
          </Label>
          <Input
            required
            name="title"
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
              const { value: val } = e.target as HTMLInputElement
              updateForm('campaignTitle', val)
            }}
            value={formData.campaignTitle}
            placeholder="What is the name of the campaign?"
          />
          <Label modalHeading id="campaign-type" optional="(optional)">
            Campaign name
          </Label>
          <SelectBoxTypeahead
            id="campaign-type"
            placeholder="Campaign name"
            value={formData.campaignGATitle}
            onInputChange={(val: string): void => {
              updateForm('campaignGATitle', val)
            }}
            items={gaCampaigns}
          />
          <Label modalHeading id="date">
            Date range
          </Label>
          <div className={styles.dateRangeWrapper}>
            <DateRange
              ranges={[selectionRange]}
              onChange={({ dates: { endDate } }: any): void => {
                updateForm(
                  'campaignEndDate',
                  moment(endDate).format('DD-MM-YYYY'),
                )
              }}
            />
          </div>
          <Label modalHeading id="notes">
            Campaign notes
          </Label>
          <Input
            required
            name="notes"
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
              const { value: val } = e.target as HTMLInputElement
              updateForm('campaignData', val)
            }}
            value={formData.campaignData}
            placeholder="Write any campaign notes here."
          />
        </ModalBody>
        <ModalFooter>
          <ConfirmationButtons
            type="simple"
            onYes={(): void => {
              if (campaign !== null) {
                onDelete(campaign.eventID, onSuccess)
                toggleActive()
              }
            }}
            onNo={(): void => {
              toggleActive()
            }}
            yesLabel={campaign !== null ? 'Delete' : 'Create campaign'}
            noLabel="Go back"
            secondaryYesLabel={campaign !== null ? 'Update' : null}
          />
        </ModalFooter>
      </form>
    </Modal>
  )
}
